import { ArrowLeftIcon, WarningCircle } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button, PasswordInput } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

export interface ResetPasswordFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  error?: string | null;
  onBack: () => void;
  email?: string | null;
}

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export function ResetPasswordForm({
  onSubmit,
  isLoading = false,
  error,
  onBack,
  email,
}: ResetPasswordFormProps) {
  const { t } = useTranslation(["auth"]);

  return (
    <div className="flex flex-col">
      {/* Back link */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-8 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        {t("auth:resetPassword.back")}
      </motion.button>

      {/* Header */}
      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("auth:resetPassword.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("auth:resetPassword.subtitle")}
          {email && <span className="font-medium text-primary"> {email}</span>}
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
        {/* New Password */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="newPassword"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:resetPassword.newPasswordLabel")}
          </label>
          <PasswordInput
            id="newPassword"
            name="newPassword"
            placeholder={t("auth:resetPassword.newPasswordPlaceholder")}
            required
            autoComplete="new-password"
            minLength={8}
            autoFocus
            showLabel={t("auth:password.show")}
            hideLabel={t("auth:password.hide")}
          />
        </motion.div>

        {/* Confirm Password */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="confirmPassword"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:resetPassword.confirmPasswordLabel")}
          </label>
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            placeholder={t("auth:resetPassword.confirmPasswordPlaceholder")}
            required
            autoComplete="new-password"
            showLabel={t("auth:password.show")}
            hideLabel={t("auth:password.hide")}
          />
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5"
          >
            <WarningCircle
              className="mt-0.5 size-4 shrink-0 text-red-500"
              weight="fill"
            />
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Submit */}
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            className="mt-1 w-full"
          >
            {t("auth:resetPassword.submit")}
          </Button>
        </motion.div>
      </form>
    </div>
  );
}
